import { useForm } from '@tanstack/react-form';
import { Button } from '@/components/ui/button';
import {
  RadioGroup,
  RadioGroupItem,
  RadioGroupItemLabel,
} from '@/components/ui/radio-group';
import type { VerifyAccountMethod } from '@/stores/store/auth-slice';
import { VERIFY_ACCOUNT_FORM } from '@/constants/auth.constants';
import { maskEmail } from '@/utils/string';
import { useMutation } from '@tanstack/react-query';
import { sendOTP } from '@/services/apis/auth/sendOTP';
import { apiMutationKeys } from '@/services/apis/apiMutationKeys';
import { useGlobalStore } from '@/stores/store';
import { FieldError } from '@/components/ui/form-field-error';
import { z } from 'zod';
import { getGlobalStore } from '@/utils/store';

const VerifyAccountSchema = z.object({
  method: z.enum(['EMAIL', 'SMS'], {
    message: 'Please select a verification method',
  }),
});

const VerifyAccountForm = () => {
  const email = useGlobalStore((store) => store.authStore.authFlow.email);
  const customerId = useGlobalStore((store) => store.authStore.customerId);
  const { setAuthFlowCurrentStep } = getGlobalStore();

  const handleSubmit = async ({
    value,
  }: {
    value: { method: VerifyAccountMethod };
  }) => {
    if (!customerId) {
      return;
    }
    try {
      await mutateAsync({
        channel: value.method,
        customerId,
      });
      setAuthFlowCurrentStep('confirmOTP');
    } catch (error) {
      // Propagate error from API to the form
      form.setFieldMeta('method', (prevMeta) => ({
        ...prevMeta,
        errorMap: {
          onChange:
            (error as Error)?.message ||
            VERIFY_ACCOUNT_FORM.FALLBACK_ERROR_MESSAGE,
        },
      }));
    }
  };
  const { mutateAsync, isPending } = useMutation({
    mutationFn: sendOTP,
    mutationKey: apiMutationKeys.sendOTP(customerId),
  });

  const form = useForm({
    defaultValues: {
      method: '' as VerifyAccountMethod,
    },
    validators: {
      onChange: VerifyAccountSchema,
    },
    onSubmit: handleSubmit,
  });
  return (
    <div className="flex flex-col gap-6 text-grey-900">
      <p className="text-xl font-nunito700">{VERIFY_ACCOUNT_FORM.TITLE}</p>
      <p className="text-base">{VERIFY_ACCOUNT_FORM.SUBTITLE}</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className="space-y-6"
      >
        <form.Field name="method">
          {(field) => (
            <RadioGroup
              value={field.state.value}
              onValueChange={(value) =>
                field.handleChange(value as VerifyAccountMethod)
              }
            >
              <div className="space-y-6">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="SMS" />
                  <RadioGroupItemLabel htmlFor="SMS">
                    {VERIFY_ACCOUNT_FORM.SMS_VERIFICATION_MESSAGE}
                  </RadioGroupItemLabel>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="EMAIL" />
                  <RadioGroupItemLabel htmlFor="EMAIL">
                    {VERIFY_ACCOUNT_FORM.EMAIL_VERIFICATION_MESSAGE(
                      maskEmail(email)
                    )}
                  </RadioGroupItemLabel>
                </div>
              </div>
              <FieldError field={field} />
            </RadioGroup>
          )}
        </form.Field>
        <form.Subscribe
          selector={(state) => [state.canSubmit]}
          children={([canSubmit]) => (
            <Button
              variant="primary"
              type="submit"
              size="medium"
              disabled={!canSubmit}
              loading={isPending}
              className="flex-none"
            >
              {VERIFY_ACCOUNT_FORM.SUBMIT_BUTTON}
            </Button>
          )}
        />
      </form>
    </div>
  );
};
export { VerifyAccountForm };
